import {memo, useEffect, useState} from 'react';

import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {selectIsUserLoggedIn, selectUserLoaded} from '~/shared/store/storeModules/user/userSelectors';
import {LocalStorageKeys} from '~/shared/consts/localStorageConsts';
import {checkTimePastWithHours} from '~/shared/utils/checkTimePast';
import {media} from '~/shared/theme/utils';
import {body16Normal} from '~/shared/theme/typography';
import renderingForRobot from '~/shared/services/renderingForRobot';
import PieCloseModalButton from '~/shared/components/Modals/styled/PieCloseModalButton';

const SIX_MONTHS_IN_DAYS = 180;

const Wrapper = styled.div`
  position: fixed;
  direction: ${({theme}) => theme.direction};
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${({theme}) => theme.zIndex.cookieConsent};
`;

const Root = styled.div`
  width: 100%;
  display: flex;
  background-color: ${({theme}) => theme.colors.backgroundInteractive};
  color: ${({theme}) => theme.colors.primaryText};
  padding: 16px;
  gap: 16px;
  ${media.minLargeMobile`
    padding: 16px 24px;
  `}
`;

const BodyText = styled.p`
  ${body16Normal}
  ${media.minLargeDesktop`
    display: inline;
    flex: 1;
  `}

  a {
    color: ${({theme}) => theme.colors.primaryText};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

const CloseButton = styled(PieCloseModalButton)`
  background: transparent;
  width: 28px;
  height: 28px;
  position: relative;
  top: auto;
  right: auto;
  left: auto;
  bottom: auto;
  align-self: flex-start;
  ${media.minLargeTablet`
     align-self: center; 
  `}

  svg path {
    fill: rgba(255, 255, 255, 0.9);
  }

  &:hover {
    background: transparent;

    svg path {
      fill: ${({theme}) => theme.colors.actionWhite};
    }
  }
`;

const CookieConsent = () => {
  const userLoaded = useSelector(selectUserLoaded);
  const isLoggedIn = useSelector(selectIsUserLoggedIn);
  const [showBanner, setShowBanner] = useState(false);
  const {t} = getLocalizationService();

  useEffect(() => {
    if (renderingForRobot) {
      return;
    }

    if (!userLoaded || isLoggedIn) {
      setShowBanner(false);
      return;
    }

    const stored = window?.localStorage?.getItem(LocalStorageKeys.COOKIE_CONSENT_TIMESTAMP);
    const is6MonthsPast = checkTimePastWithHours({storedDate: stored, days: SIX_MONTHS_IN_DAYS});

    setShowBanner(!stored || is6MonthsPast);
  }, [userLoaded, isLoggedIn]);

  const handleConfirm = () => {
    if (window?.localStorage) {
      window.localStorage.setItem(LocalStorageKeys.COOKIE_CONSENT_TIMESTAMP, String(new Date()));
    }
    setShowBanner(false);
  };

  if (renderingForRobot) {
    return null;
  }

  return (
    <Wrapper data-test-id="cookie-consent">
      {showBanner && (
        <Root data-test-id="cookie-consent-content">
          <BodyText
            data-test-id="cookie-consent-body"
            dangerouslySetInnerHTML={{__html: t('cookie_consent_body')}}
          />
          <CloseButton onClick={handleConfirm} dataTestId="cookie-consent-confirm" />
        </Root>
      )}
    </Wrapper>
  );
};

export default memo(CookieConsent);
